import React, { Fragment, useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { useParams, useLocation } from 'react-router-dom';
import qs from 'query-string';
import classNames from 'classnames';
import compose from 'lodash.flowright';
import { useQuery } from 'react-apollo';

import AccountCardConfigurator from '../../../../services/cards/views/AccountCardConfigurator';
import PhoneAuthModal from '../../../../components/auth/PhoneAuthModal';
import {
  CARD_ELEMENT_TYPE,
  NATIVE_APP_LAYOUT,
} from '../../../../services/cards/constants';
import Header from '../../../../components/global/Header';
import Logo from '../../../../components/global/Logo';
import getConfig from '../../../../util/config';
import useIsNativeWebView from '../../../../services/native-bridge/hooks/useIsWebView';
import withGoogleRecaptchaProvider from '../../../../components/auth/hocs/withGoogleRecaptchaProvider';
import getCardCategoriesQuery from '../../../../services/cards/graphql/queries/getCardCategories';
import { PUSH_NOTIFICATION_TYPES } from '../../../../services/cards/components/CardCategories/constants';
import NativeActivityIndicator from '../../../../components/global/NativeActivityIndicator';
import useAccountCardNativeFeatureSupport from '../../../../services/cards/hooks/useAccountCardNativeFeatureSupport';
import useEdgeToEdgeViewport from '../../../../services/cards/hooks/useEdgeToEdgeViewport';

const AccountCardPublicConfigurator = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const { cardCategoryId } = useParams();
  const { photoId, accountCardId } = qs.parse(location.search);
  const isNativeWebView = useIsNativeWebView(true);
  const { nativeSupportsLayout } = useAccountCardNativeFeatureSupport();
  const isEdgeToEdge = nativeSupportsLayout(NATIVE_APP_LAYOUT.EDGE_TO_EDGE);
  const { data: { cardCategories: holidayCategories } = {} } = useQuery(
    getCardCategoriesQuery,
    {
      variables: {
        push_notification_type: PUSH_NOTIFICATION_TYPES.HOLIDAYS,
      },
    },
  );
  const {
    data: { cardCategories: selectedCategories } = {},
    loading: loadingSelectedCategory,
  } = useQuery(getCardCategoriesQuery, {
    variables: {
      root_only: false,
      card_category_id: cardCategoryId,
    },
  });

  const config = getConfig();
  // If this category exists, it is the parent holiday category.
  const holidayCategory = holidayCategories?.records?.[0];
  const selectedCategory = selectedCategories?.records?.[0];

  useEdgeToEdgeViewport(isEdgeToEdge);

  useEffect(() => {
    if (holidayCategory) {
      setLoading(false);
    }
  }, [holidayCategory]);

  if (loading || loadingSelectedCategory) {
    return (
      <div className="full-screen-loader">
        <NativeActivityIndicator />
      </div>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>Send a card &ndash; Waldo</title>
        <body
          className={classNames({
            'body-public-account-card': true,
            'is-native-web-view': isNativeWebView,
            'is-edge-to-edge': isEdgeToEdge,
          })}
        />
      </Helmet>

      <div className="public-account-card-wrap">
        <Header className="public-account-card-header">
          <Logo linksTo={config.marcomUrl} externalLink />
          <h1 className="public-account-card-header-title">
            Create and mail a personalized card in a few taps!
          </h1>
        </Header>

        <AccountCardConfigurator
          title=""
          subtitle=""
          className="pt-4"
          defaultCardCategoryId={
            holidayCategory && cardCategoryId === holidayCategory?.id
              ? undefined
              : cardCategoryId
          }
          defaultParentCardCategoryId={
            holidayCategory && cardCategoryId === holidayCategory?.id
              ? holidayCategory.id
              : selectedCategory?.parent?.id
          }
          accountCardId={accountCardId}
          presetCardElementConfigurationInputs={
            photoId
              ? [
                  {
                    cardElement: {
                      type: CARD_ELEMENT_TYPE.PHOTO,
                    },
                    cardElementConfiguration: {
                      photo_id: photoId,
                    },
                  },
                ]
              : undefined
          }
          resetSelectedCategoryOnClose={false}
        />
      </div>

      <PhoneAuthModal enableEmailFallback={false} />
    </Fragment>
  );
};

export default compose(withGoogleRecaptchaProvider)(
  AccountCardPublicConfigurator,
);
